import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  useHolidays,
  useManagerAttendance,
  useManagerLeaveSummary,
  useManagerTeam,
  useTeamLeave,
} from "@/api/queries";
import { useSession } from "@/api/session";
import { PageHeader } from "@/app/page-header";
import { NotificationsPanel } from "@/components/notifications-panel";
import { CategoryBarChart, StatTile, TrendChart } from "@/components/charts";
import { STATUS_SERIES_COLOR } from "@/components/charts/stacked-bar-chart";
import { AttendanceStatusBadge } from "@/pages/hr/attendance";
import { Avatar } from "@/components/ui/avatar";
import { FiledLateBadge, StatusBadge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Panel, PanelHeader, SectionHeading } from "@/components/ui/panel";
import { EmptyState, SkeletonTable } from "@/components/ui/states";
import { attendanceRate, countBy, tallyByDay } from "@/lib/analytics";
import { classifyDay, holidayMap, isRestDay, leaveMap, shiftDate, todayStr, wfhMap } from "@/lib/calendar";
import { formatDate, formatDays } from "@/lib/format";
import { leaveLabel, WORK_MODE_LABEL } from "@/lib/labels";
import { WfhWeekCharts } from "@/pages/shared/wfh-charts";

function recentRange() {
  const end = todayStr();
  return { start: shiftDate(end, -13), end };
}

export function ManagerOverviewPage() {
  const user = useSession((s) => s.user);
  const { start, end } = useMemo(recentRange, []);

  const team = useManagerTeam();
  const attendance = useManagerAttendance(start, end);
  const leave = useTeamLeave();
  const summary = useManagerLeaveSummary();
  const holidays = useHolidays();

  const holidayNames = useMemo(() => holidayMap(holidays.data), [holidays.data]);
  const members = team.data?.members ?? [];
  const coManagers = team.data?.coManagers ?? [];
  const people = attendance.data?.users ?? [];
  const records = attendance.data?.records ?? [];

  // Per-day leave, indexed by person-day. Distinct from `leave` above, which is
  // the team's REQUEST list — this is those requests expanded onto the calendar.
  const leaveDays = useMemo(() => leaveMap(attendance.data?.leave), [attendance.data?.leave]);
  // Approved work from home, indexed the same way. Without it this page called a
  // planned WFH day something else than the manager's own attendance page did
  // for the same person on the same date.
  const wfhDays = useMemo(() => wfhMap(attendance.data?.wfh), [attendance.data?.wfh]);

  const dayTallies = useMemo(
    () => tallyByDay(people, records, start, end, holidayNames, leaveDays),
    [people, records, start, end, holidayNames, leaveDays],
  );
  const rate = attendanceRate(dayTallies);

  const today = todayStr();
  const todayByUser = useMemo(() => {
    const map = new Map<number, (typeof records)[number]>();
    for (const record of records) if (record.date === today) map.set(record.user_id, record);
    return map;
  }, [records, today]);

  const pending = (leave.data ?? []).filter((r) => r.status === "pending");
  const byWorkMode = useMemo(
    () => countBy(members, (m) => WORK_MODE_LABEL[m.work_mode]),
    [members],
  );

  const compOffOutstanding = (summary.data?.members ?? []).reduce(
    (sum, member) => sum + member.compOff.available,
    0,
  );

  const trendData = dayTallies.map((tally) => ({
    day: formatDate(tally.date, "d MMM") ?? tally.date,
    complete: tally.complete,
    leave: tally.leave,
    absent: tally.absent,
  }));

  // No team mapped is a real, distinguishable state, not an error.
  if (!team.isLoading && !team.data?.team) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader eyebrow="Scoped to your direct reports" title="Team overview" />
        <Panel>
          <EmptyState
            icon="team"
            title="Your account is not mapped to a team yet"
            body="A manager account only sees a team once HR maps it to one. Ask HR and Admin to set the team on your account, then sign in again."
          />
        </Panel>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Scoped to your direct reports"
        title={`Welcome, ${user?.name?.split(" ")[0] ?? "there"}`}
        sub={
          team.data?.team
            ? `${team.data.team.name} · ${members.length} ${members.length === 1 ? "direct report" : "direct reports"}` +
              (coManagers.length > 0
                ? ` · Co-managed with ${coManagers.map((m) => m.name).join(", ")}`
                : "")
            : undefined
        }
        actions={
          <Button asChild variant="primary" iconRight="arrow-right">
            <Link to="/manager/leave">Review leave</Link>
          </Button>
        }
      />

      <NotificationsPanel />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile label="Direct reports" value={members.length} icon="team" loading={team.isLoading} />
        <StatTile
          label="Full days, last 14 days"
          value={rate === null ? "—" : `${rate}%`}
          icon="attendance"
          loading={attendance.isLoading}
          meta={rate === null ? "Nothing recorded yet" : "of working-day slots"}
        />
        <StatTile
          label="Leave to action"
          value={pending.length}
          icon="warning-circle"
          loading={leave.isLoading}
          meta={pending.length === 0 ? "Nothing waiting on you" : "Waiting on your decision"}
        />
        <StatTile
          label="Team comp off"
          value={formatDays(compOffOutstanding)}
          icon="calendar-check"
          loading={summary.isLoading}
          meta="Earned and not yet taken"
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <TrendChart
          title="Team attendance"
          note="Last 14 working days, excluding weekly offs and company holidays"
          data={trendData}
          xKey="day"
          series={[
            { key: "complete", label: "Full day", color: STATUS_SERIES_COLOR.complete },
            { key: "leave", label: "On leave", color: STATUS_SERIES_COLOR.leave },
            { key: "absent", label: "No record", color: STATUS_SERIES_COLOR.absent },
          ]}
          loading={attendance.isLoading}
          {...(attendance.error ? { error: attendance.error.message } : {})}
          emptyBody="Nobody on your team has recorded attendance in this window yet."
        />

        <CategoryBarChart
          title="How your team works"
          note="Work mode configured on each account"
          data={byWorkMode}
          valueHeader="People"
          loading={team.isLoading}
          emptyBody="No direct reports mapped to your team yet."
        />
      </div>

      {/* How much of the team is working from home, and whether that is
          changing. Sits on the overview rather than only on the leave page
          because it is a pattern to notice, not a queue to clear — and the one
          thing a per-request list cannot show. */}
      <SectionHeading
        title="Work from home"
        note="Approved days only, counted the same way leave is: weekends and company holidays inside a request are not days worked from home."
        actions={
          <Button asChild variant="ghost" size="sm" iconRight="arrow-right">
            <Link to="/manager/leave">Review requests</Link>
          </Button>
        }
      />
      <WfhWeekCharts scopeLabel="your team" />

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel className="min-w-0">
          <PanelHeader
            title="Today"
            note={formatDate(today) ?? undefined}
            actions={
              <Button asChild variant="ghost" size="sm" iconRight="arrow-right">
                <Link to="/manager/attendance">Attendance</Link>
              </Button>
            }
          />
          {attendance.isLoading ? (
            <SkeletonTable rows={4} cols={3} />
          ) : members.length === 0 ? (
            <EmptyState
              icon="team"
              title="No direct reports yet"
              body="Employees appear here once HR maps their account to your team."
            />
          ) : isRestDay(today, new Set(holidayNames.keys())) ? (
            <EmptyState
              icon="holiday"
              title="Today is a weekly off"
              body={holidayNames.get(today) ?? "No attendance is expected today."}
            />
          ) : (
            <ul className="divide-y divide-line">
              {members.map((member) => {
                const record = todayByUser.get(member.id);
                const onLeave = leaveDays.get(`${member.id}|${today}`);
                const kind = classifyDay(
                  today,
                  record,
                  holidayNames,
                  onLeave,
                  wfhDays.get(`${member.id}|${today}`),
                );
                return (
                  <li key={member.id} className="flex items-center gap-3 px-4 py-2.5">
                    <Avatar name={member.name} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink">{member.name ?? "Unnamed"}</p>
                      <p className="truncate text-xs text-muted">{member.designation}</p>
                    </div>
                    <AttendanceStatusBadge kind={kind} leave={onLeave} />
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        <Panel className="min-w-0">
          <PanelHeader
            title="Leave waiting on you"
            actions={
              <Button asChild variant="ghost" size="sm" iconRight="arrow-right">
                <Link to="/manager/leave">All requests</Link>
              </Button>
            }
          />
          {leave.isLoading ? (
            <SkeletonTable rows={4} cols={3} />
          ) : pending.length === 0 ? (
            <EmptyState
              icon="check-circle"
              title="Nothing waiting on you"
              body="Every request from your team has been actioned."
            />
          ) : (
            <ul className="divide-y divide-line">
              {pending.slice(0, 5).map((request) => (
                <li key={request.id} className="flex items-center gap-3 px-4 py-3">
                  <Avatar name={request.employee_name} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">
                      {request.employee_name ?? "Unnamed"}
                    </p>
                    <p className="truncate text-xs text-muted">
                      {leaveLabel(request.leave_type)} · {formatDate(request.start_date, "d MMM")}
                    </p>
                  </div>
                  <FiledLateBadge request={request} />
                  <StatusBadge status={request.status} />
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
